import React from "react";

/**
 * Root-level error boundary. Catches any uncaught render/lifecycle error
 * in the React tree and renders a friendly recovery screen instead of a
 * blank page. Reloading the app clears in-memory state; if the crash was
 * caused by a stale SW/cached bundle, the fresh load often fixes it.
 *
 * Deliberately simple: no external logging service, no telemetry. Errors
 * still surface in the browser console for debugging.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        data-testid="error-boundary-fallback"
        className="min-h-screen flex items-center justify-center bg-slate-50 p-6"
      >
        <div className="max-w-lg w-full bg-white rounded-lg shadow-lg p-6 text-center space-y-4">
          <div className="text-4xl">⚠️</div>
          <h1 className="text-2xl font-semibold text-slate-800">
            Se produjo un error inesperado
          </h1>
          <p className="text-slate-600 text-sm">
            La aplicación encontró un problema y no pudo continuar. Al recargar
            se restablece el estado — tus datos guardados no se ven afectados.
          </p>
          {this.state.error?.message && (
            <p className="text-xs text-slate-600 font-mono break-words">
              {String(this.state.error.message).slice(0, 240)}
            </p>
          )}
          <button
            data-testid="error-boundary-reload-btn"
            onClick={this.handleReload}
            className="mt-2 px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
          >
            Recargar la aplicación
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
